<template>
  <Layout>
    <!-- <PageHeader v-if="title" :title="title" :items="items" /> -->

    <Loader :loading="loadingExport" no-axios>
      <div class="">
        <div v-if="stats.length" class="row">
          <div class="title">{{ lodash.upperCase(title) }}</div>
          <div v-for="stat of stats" :key="stat.icon" :class="stat.wrapperClass">
            <Stat v-bind="stat"/>
          </div>
        </div>
        <div v-if="!lodash.isEmpty(filterColumns)">
          <model-filter
              v-model="filter"
              :columns="filterColumns"
              :filterHandler="filterHandler"
              :modelVariable="modelVariable"
          />
        </div>
      </div>
      <div class="title">{{ lodash.upperCase($t("List")) }} {{ lodash.upperCase(title) }}</div>
      <div class="row">
        <model-index
            ref="table"
            :action-buttons="actionButtons"
            :actions="actions"
            :busy.sync="busy"
            :columns="columns"
            :filter-params.sync="filterParams"
            :from="from"
            :loading="loading"
            :models-paginated="modelsPaginated"
            :page.sync="page"
            :per-page.sync="perPage"
            :search.sync="search"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :title="undefined"
            :total="totalFiltered"
            class="col-12"
            v-bind="modelIndexProps"
            @create-click="handleCreate"
            @export-click="handleExport"
            @show-click="handleShow"
            @edit-click="handleEdit"
            @delete-click="handleDelete"
        >
          <template #card-tools="{ denyCreate, emitCreateClick }">
            <div class="d-flex flex-wrap align-items-center gap-2">
              <b-button
                  v-if="!denyCreate"
                  class="me-2"
                  size="lg"
                  style="font-size: 14px"
                  variant="primary"
                  @click="emitCreateClick"
              >
                {{ $t("Create New") }}
              </b-button>
              <b-button
                  :disabled="isAnyBulkActionLoading || !hasSelectedSalesPlans"
                  class="me-2"
                  size="lg"
                  style="font-size: 14px"
                  variant="success"
                  @click="handleBulkApprove"
              >
                <i v-if="bulkApproveLoading" class="bx bx-loader bx-spin me-2"></i>
                {{ lodash.startCase($t('sales_plans.buttons.approve')) }}
                <span v-if="hasSelectedSalesPlans">({{ selectedSalesPlanIds.length }})</span>
              </b-button>
              <b-button
                  :disabled="isAnyBulkActionLoading || !hasSelectedSalesPlans"
                  class="me-2"
                  size="lg"
                  style="font-size: 14px"
                  variant="danger"
                  @click="handleBulkReject"
              >
                <i v-if="bulkRejectLoading" class="bx bx-loader bx-spin me-2"></i>
                {{ lodash.startCase($t('sales_plans.buttons.reject')) }}
                <span v-if="hasSelectedSalesPlans">({{ selectedSalesPlanIds.length }})</span>
              </b-button>
            </div>
          </template>
          <template #action="{ data }">
            <b-dropdown-item v-if="!data.item['_denyShow']" @click="handleShow(data.item)">
              <i class="bx bx-show font-size-16 align-middle me-1 pb-1"></i>
              {{ $t("Detail") }}
            </b-dropdown-item>
            <b-dropdown-item
                v-for="actionButton in getVisibleActionButtons(data.item)"
                :key="actionButton.key"
                :disabled="actionButton.disable(data.item)"
                @click="actionButton.action(data.item)"
            >
              <i :class="['bx', actionButton.icon, 'font-size-16 align-middle me-1 pb-1']"></i>
              {{ actionButton.label }}
            </b-dropdown-item>
            <b-dropdown-item v-if="!data.item['_denyEdit']" @click="handleEdit(data.item)">
              <i class="bx bx-edit font-size-16 align-middle me-1 pb-1"></i>
              {{ $t("Edit") }}
            </b-dropdown-item>
            <b-dropdown-item v-if="!data.item['_denyDelete']" @click="handleDelete(data.item)">
              <i class="bx bx-trash font-size-16 align-middle me-1 pb-1"></i>
              <span class="">{{ $t("Delete") }}</span>
            </b-dropdown-item>
          </template>
        </model-index>
      </div>

      <sales-plan-approve-confirmation
          ref="sales-plan-approve-confirmation"
      />
      <sales-plan-reject-confirmation
          ref="sales-plan-reject-confirmation"
      />
      <sales-plan-cancel-confirmation
          ref="sales-plan-cancel-confirmation"
          @cancelled="onCancelled"
      />
      <sales-plan-bulk-action-success
          ref="sales-plan-bulk-action-success"
          @hidden="handleBulkActionResultModalHidden"
      />
      <sales-plan-bulk-action-partial-success
          ref="sales-plan-bulk-action-partial-success"
          @hidden="handleBulkActionResultModalHidden"
      />
      <b-modal
          v-model="duplicateConfirmModalShow"
          centered
          footer-class="border-0 pt-0"
          hide-header
          no-close-on-backdrop
          no-close-on-esc
        >
          <div class="text-center">
            <h4>{{ duplicateConfirmTitle() }}?</h4>
            <p class="m-0">{{ duplicateConfirmDescription() }}</p>
          </div>
          <template #modal-footer>
            <div class="d-flex justify-content-end gap-2 w-100">
              <b-button
                  :disabled="duplicateLoading"
                  block
                  variant="light"
                  @click="closeDuplicateConfirmModal"
              >
                {{ $t('Cancel') }}
              </b-button>
              <b-button
                  :disabled="duplicateLoading"
                  block
                  class="m-0"
                  variant="primary"
                  @click="confirmDuplicate"
              >
                <b-spinner v-if="duplicateLoading" class="me-2" small></b-spinner>
                {{ lodash.startCase($t('sales_plans.buttons.continue_duplicate')) }}
              </b-button>
            </div>
          </template>
        </b-modal>
    </Loader>
  </Layout>
</template>

<script>
import ModelFilter from "@/router/views/sales_plans/filter";
import ModelIndex from "../models/index-v3";
import {amountScaleFormatter, currencyFormatter, decimalFormatter, percentageFormatter} from "@/helpers/ui/formatters";
import {salesPlanDisplayIdLabel, salesPlanDisplayLabel} from "@/helpers/ui/salesPlanLabels";
import {hasAllowedAction as hasAllowedActionHelper, normalizeAllowedActions} from "@/helpers/ui/allowedActions";
import StatusWidget from "@/components/widgets/statuses/status";
import SalesPlanApproveConfirmation from "@/components/modals/sales-plan-approve-confirmation.vue";
import SalesPlanRejectConfirmation from "@/components/modals/sales-plan-reject-confirmation.vue";
import SalesPlanCancelConfirmation from "@/components/modals/sales-plan-cancel-confirmation.vue";
import SalesPlanBulkActionSuccess from "@/components/modals/sales-plan-bulk-action-success.vue";
import SalesPlanBulkActionPartialSuccess from "@/components/modals/sales-plan-bulk-action-partial-success.vue";
import salesPlanDuplicate from "@/components/mixins/sales-plan-duplicate";
import { expandSalesPlanStatusFilterValue } from "@/utils/salesPlanFilters";
import {serialize} from "object-to-formdata";
import Swal from "sweetalert2";

export default {
  extends: ModelIndex,
  mixins: [salesPlanDuplicate],
  components: {
    ModelFilter,
    SalesPlanApproveConfirmation,
    SalesPlanRejectConfirmation,
    SalesPlanCancelConfirmation,
    SalesPlanBulkActionSuccess,
    SalesPlanBulkActionPartialSuccess,
  },
  page: {
    title: "Sales Plans"
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t("sales_plans.singular"));
      }
    },
    resourceRoute: {default: "sales_plans"},
    modelVariable: {default: "sales_plan"},
    denyCreate: {
      type: Boolean,
      default() {
        return !this.$auth.hasPermissionTo("sale_plans.create");
      }
    }
  },
  data() {
    return {
      title: this.lodash.startCase(this.$t("sales_plans.plural")),
      items: [
        {
          text: this.lodash.startCase(this.$t("sales_plans.plural")),
          href: "/sales_plans",
          active: true
        }
      ],
      sortBy:
          (this.$route.query.sort &&
              this.$route.query.sort
                  .split("|")
                  .shift()
                  .split(",")
                  .shift()) ||
          "id",
      sortDesc:
          (this.$route.query.sort &&
              this.$route.query.sort
                  .split("|")
                  .shift()
                  .split(",")
                  .pop() === "desc") ||
          true,
      filterColumns: {
        period: {
          type: Array,
          label: this.lodash.upperFirst(this.$t("sales_plans.period")),
          placeholder: "Pilih " + this.lodash.upperFirst(this.$t("sales_plans.period")),
          operator: "between",
          input_component: "datepicker-range"
        },
        processing_id: {
          type: Array,
          label: this.lodash.upperFirst(this.$t("sales_plans.processing")),
          placeholder: this.$t("Select {something}", {something: this.lodash.upperFirst(this.$t("sales_plans.processing"))}),
          operator: "in",
          input_component: "processing-customer-multiselect-ajax"
        },
        partner_id: {
          type: Array,
          label: this.lodash.upperFirst(this.$t("sales_plans.partner")),
          placeholder: this.$t("Select {something}", {something: this.lodash.upperFirst(this.$t("sales_plans.partner"))}),
          operator: "in",
          input_component: "partner-customer-multiselect-ajax"
        },
        status: {
          type: String,
          label: this.lodash.upperFirst(this.$t("sales_plans.status")),
          placeholder: this.$t("Select {something}", {something: this.lodash.upperFirst(this.$t("sales_plans.status"))}),
          input_component: "sales-plan-status-select"
        }
      },
      model: this.modelVariable,
      models: [],
      selectedSalesPlanIds: [],
      selectedSalesPlanApprovalLevels: {},
      bulkApproveLoading: false,
      bulkRejectLoading: false,
      duplicateLoading: false,
      duplicateConfirmModalShow: false,
      allowedActions: [],
      stats: [],
    };
  },
  computed: {
    modelIndexProps() {
      return {
        ...this.$props,
        denyCreate: this.denyCreateFromAllowedActions,
      };
    },
    actions() {
      return [];
    },
    actionButtons() {
      return [
        {
          key: "duplicate",
          icon: "bx-copy-alt",
          action: (value) => this.handleDuplicate(value),
          label: this.lodash.startCase(this.$t("sales_plans.buttons.duplicate")),
          disable: () => this.duplicateLoading,
          hide: () => this.denyCreateFromAllowedActions,
        },
        {
          key: "cancel",
          icon: "bx-x",
          action: (value) => this.handleCancel(value),
          label: this.lodash.startCase(this.$t("sales_plans.buttons.cancel")),
          disable: () => false,
          hide: (value) => !this.hasAllowedAction(value, ["cancel"]),
        },
      ];
    },
    hasSelectedSalesPlans() {
      return this.selectedSalesPlanIds.length > 0;
    },
    isAnyBulkActionLoading() {
      return this.bulkApproveLoading || this.bulkRejectLoading;
    },
    denyCreateFromAllowedActions() {
      return !hasAllowedActionHelper(this.allowedActions, ["create", "store"]);
    },
    columns() {
      return {
        [this.modelVariable || "sales_plan"]: [
          {
            key: "bulk_select",
            label: "",
            sortable: false,
            class: "text-center",
            thStyle: {width: "1%"},
            tdClass: "align-middle text-center",
            component: {
              props: ["item"],
              methods: {
                onToggle(checked) {
                  this.$emit("toggle-selection", {
                    item: this.item,
                    checked
                  });
                }
              },
              template: `
                <b-form-checkbox
                    v-if="item._canBulkSelect"
                    :checked="item._selected"
                    @change="onToggle"
                    :aria-label="'Select sales plan ' + item.id"
                />
              `
            },
            componentListeners: {
              "toggle-selection": payload => this.handleSalesPlanSelection(payload)
            }
          },
          {
            key: "id",
            label: this.$t("sales_plans.sales_plan_id"),
            sortable: true,
            style: {width: "125px"},
            tdClass: "align-middle",
            formatter: (_, __, item) => this.formatSalesPlanDisplayId(item),
          },
          {
            key: "period",
            label: this.lodash.startCase(this.$t("sales_plans.period")),
            component: {
              props: ['item'],
              computed: {
                formattedPeriodText() {
                  return `${this.$moment(this.item.period_start).format('DD MMM YYYY')} - ${this.$moment(this.item.period_end).format('DD MMM YYYY')}`
                }
              },
              template: `
                <div>{{ formattedPeriodText }}</div>`
            }
          },
          {
            key: "product.name",
            label: this.lodash.startCase(this.$t("sales_plans.product")),
            sortable: true,
            tdClass: 'align-middle',
          },
          {
            key: "partner.name",
            label: this.lodash.startCase(this.$t("sales_plans.partner")),
            sortable: true,
            tdClass: 'align-middle',
            formatter: value => value ?? "-"
          },
          {
            key: "processing.name",
            label: this.lodash.startCase(this.$t("sales_plans.processing")),
            sortable: true,
            tdClass: 'align-middle',
            formatter: value => value ?? "-"
          },
          {
            key: "estimated_sale",
            label: this.lodash.startCase(this.$t("sales_plans.estimated_sale")),
            tdClass: "align-middle text-end",
            formatter: value => value != null ? currencyFormatter(value) : "-"
          },
          {
            key: "realized_amount",
            label: this.lodash.startCase(this.$t("sales_plans.realization_amount")),
            tdClass: "align-middle text-end",
            formatter: value => value != null ? currencyFormatter(value) : "-"
          },
          {
            key: "realized_amount_ratio",
            label: `${this.lodash.startCase(this.$t("sales_plans.realization"))} %`,
            tdClass: "align-middle text-end",
            formatter: value => value != null ? percentageFormatter(value) : "-"
          },
          {
            key: "estimated_margin",
            label: this.lodash.startCase(this.$t("sales_plans.estimated_margin")),
            tdClass: "align-middle text-end",
            formatter: value => value != null ? currencyFormatter(value) : "-"
          },
          {
            key: "estimated_margin_ratio",
            label: `${this.lodash.startCase(this.$t("sales_plans.estimated_margin"))} %`,
            tdClass: "align-middle text-end",
            formatter: value => value != null ? percentageFormatter(value) : "-"
          },
          {
            key: "status",
            label: this.lodash.startCase(this.$t("sales_plans.status")),
            tdClass: "align-middle",
            component: {
              props: ["item"],
              template: `
                <status-badge
                    v-if="item.status"
                    entity="sales_plan"
                    type="status"
                    :status="item.status"
                ></status-badge>
                <span v-else>-</span>
              `
            },
          },
          {key: "action", label: this.$t("Action"), class: "text-center", style: {width: "1%"}}
        ]
      };
    }
  },
  methods: {
    currencyFormatter,
    decimalFormatter,
    salesPlanDisplayLabel,
    formatSalesPlanDisplayId(item) {
      return salesPlanDisplayIdLabel(item, (key, params) => this.$t(key, params));
    },
    getModelName(value) {
      return value["id"];
    },
    getVisibleActionButtons(item) {
      return this.actionButtons.filter(actionButton => !actionButton.hide?.(item));
    },
    handleSalesPlanSelection({item, checked}) {
      const id = this.getModelKey(item);
      // if (id == null || !this.canBulkSelect(item)) return;

      item._selected = checked;
      if (checked) {
        this.$set(this.selectedSalesPlanApprovalLevels, id, this.getSalesPlanApprovalLevel(item));
        if (!this.selectedSalesPlanIds.includes(id)) {
          this.selectedSalesPlanIds = [...this.selectedSalesPlanIds, id];
        }
        return;
      }

      this.selectedSalesPlanIds = this.selectedSalesPlanIds.filter(selectedId => selectedId !== id);
      this.$delete(this.selectedSalesPlanApprovalLevels, id);
    },
    clearSelectedSalesPlans() {
      this.selectedSalesPlanIds = [];
      this.selectedSalesPlanApprovalLevels = {};
      this.models = (this.models || []).map(model => ({
        ...model,
        _selected: false
      }));
    },
    buildActionPayload({ids = [], cashflow = null, description = null, reason = null, file = null} = {}) {
      const payload = {};

      if (Array.isArray(ids) && ids.length > 0) {
        payload.ids = ids;
        payload.sales_plan_ids = ids;
      }

      if (cashflow !== null && cashflow !== undefined) {
        payload.cashflow = cashflow;
      }

      const resolvedText = [description, reason].find((value) => {
        return value !== null && value !== undefined && `${value}`.trim() !== '';
      });

      if (resolvedText !== undefined) {
        const text = `${resolvedText}`.trim();
        payload.description = text;
        payload.reason = text;
      }

      if (file) {
        payload.file = file;
      }

      if (Object.keys(payload).length === 0) {
        return {};
      }

      return serialize(payload, {indices: true, booleansAsIntegers: true});
    },
    getAllowedActions(model) {
      return normalizeAllowedActions(model?.allowed_actions);
    },
    getSalesPlanApprovalLevel(model) {
      const level = model?.current_approval_level
          ?? model?.approval_progress?.current_level
          ?? model?.approval_progress?.current_step?.level
          ?? model?.approval_progress?.steps?.find(step => step?.status === "current")?.level;
      const numericLevel = Number(level);

      return Number.isFinite(numericLevel) ? numericLevel : null;
    },
    getSelectedSalesPlanApprovalLevels() {
      return [...new Set(this.selectedSalesPlanIds
          .map(id => this.selectedSalesPlanApprovalLevels[id])
          .filter(level => level !== null && level !== undefined))];
    },
    hasMixedSalesPlanApprovalLevels() {
      return this.getSelectedSalesPlanApprovalLevels().length > 1;
    },
    showMixedApprovalLevelWarning() {
      return Swal.fire({
        title: `${this.$t("sales_plans.modal_messages.bulk_approve_mixed_levels_title")}`,
        text: this.$t("sales_plans.modal_messages.bulk_approve_mixed_levels_description"),
        type: "warning",
        confirmButtonClass: "btn btn-warning btn-fill",
        buttonsStyling: false,
      });
    },
    hasAllowedAction(model, actions) {
      return hasAllowedActionHelper(model, actions);
    },
    canBulkSelect(model) {
      return this.hasAllowedAction(model, ["approve", "reject"]);
    },
    getActionLabel(action) {
      return this.lodash.startCase(this.$t(`sales_plans.buttons.${action}`));
    },
    getBulkActionModalRef(action) {
      return action === "approve"
          ? "sales-plan-approve-confirmation"
          : "sales-plan-reject-confirmation";
    },
    getBulkActionConfirmTitle(action, total) {
      const key = action === "approve"
          ? "sales_plans.modal_messages.bulk_confirm_approve_title"
          : "sales_plans.modal_messages.bulk_confirm_reject_title";
      return this.$t(key, {count: total});
    },
    getBulkActionConfirmMessage(action, total) {
      const key = action === "approve"
          ? "sales_plans.modal_messages.bulk_confirm_approve_description"
          : "sales_plans.modal_messages.bulk_confirm_reject_description";
      return this.$t(key, {count: total});
    },
    getBulkActionSuccessTitle(action) {
      const key = action === "approve"
          ? "sales_plans.modal_messages.bulk_approve_success_title"
          : "sales_plans.modal_messages.bulk_reject_success_title";
      return this.$t(key);
    },
    getBulkActionSuccessMessage(action, total) {
      const key = action === "approve"
          ? "sales_plans.modal_messages.bulk_approve_success_message"
          : "sales_plans.modal_messages.bulk_reject_success_message";
      return this.$t(key, {count: total});
    },
    getBulkActionPartialSuccessTitle(action, summary = {}) {
      const successCount = Number(summary?.successCount || 0);
      if (successCount <= 0) {
        const failedKey = action === "approve"
            ? "sales_plans.modal_messages.bulk_approve_failed_title"
            : "sales_plans.modal_messages.bulk_reject_failed_title";
        return this.$t(failedKey);
      }

      const key = action === "approve"
          ? "sales_plans.modal_messages.bulk_approve_partial_success_title"
          : "sales_plans.modal_messages.bulk_reject_partial_success_title";
      return this.$t(key);
    },
    getBulkActionPartialSuccessMessage(action, successCount, total) {
      const key = action === "approve"
          ? "sales_plans.modal_messages.bulk_approve_partial_success_message"
          : "sales_plans.modal_messages.bulk_reject_partial_success_message";
      return this.$t(key, {
        success_count: successCount,
        total,
      });
    },
    getBulkActionEndpoint(action) {
      return action === "approve"
          ? "/v1/sales-plans/bulk-approve"
          : "/v1/sales-plans/bulk-reject";
    },
    getBulkActionResponseData(response) {
      return response?.data?.data ?? {};
    },
    getBulkActionTotalCount(summary) {
      return Number(summary?.totalSelected || 0);
    },
    getBulkActionItemId(item) {
      if (item && typeof item === "object") {
        return this.getModelKey(item) ?? item.id ?? null;
      }

      return item ?? null;
    },
    getBulkActionItemLabel(item) {
      const id = this.getBulkActionItemId(item);
      const model = this.models.find((model) => this.getModelKey(model) === id);
      if (!model) {
        return `ID ${id}`;
      }

      return this.salesPlanDisplayLabel(model);
    },
    normalizeBulkActionText(value) {
      if (Array.isArray(value)) {
        return value
            .map((entry) => this.normalizeBulkActionText(entry))
            .filter((entry) => entry !== null && entry !== undefined && `${entry}`.trim() !== "")
            .join(", ");
      }

      if (value && typeof value === "object") {
        return this.normalizeBulkActionText(
            value.message
            ?? value.errors
        );
      }

      if (value === null || value === undefined) {
        return null;
      }

      const text = `${value}`.trim();
      return text.length > 0 ? text : null;
    },
    extractBulkActionItemReason(item, reasonMap = null, fallbackReason = null) {
      const id = this.getBulkActionItemId(item);
      const mappedReason = reasonMap && typeof reasonMap === "object"
          ? reasonMap[id] ?? reasonMap[String(id)]
          : null;

      return this.normalizeBulkActionText(
          (item && typeof item === "object")
              ? item.message
              ?? item.errors
              : null
      ) || this.normalizeBulkActionText(mappedReason) || this.normalizeBulkActionText(fallbackReason);
    },
    normalizeBulkActionDisplayItems(items, status, reasonMap = null, fallbackReason = null) {
      if (!Array.isArray(items)) {
        return [];
      }

      return items
          .map((item) => {
            const id = this.getBulkActionItemId(item);
            if (id === null || id === undefined || id === "") {
              return null;
            }

            return {
              id,
              status,
              label: this.getBulkActionItemLabel(item),
              reason: this.extractBulkActionItemReason(item, reasonMap, fallbackReason),
            };
          })
          .filter(Boolean);
    },
    buildBulkActionResultItems(summary, action) {
      const fallbackMessage = summary?.message || null;
      const failedReasonMap = this.lodash.get(summary?.data, "errors") || null;
      const successItems = this.normalizeBulkActionDisplayItems(
          summary?.successItems || summary?.data?.success_items,
          "success"
      );
      const failedItems = this.normalizeBulkActionDisplayItems(
          summary?.failedItems || summary?.data?.failed_items,
          "warning",
          failedReasonMap,
          fallbackMessage
      );

      return [...successItems, ...failedItems].map((item, index) => ({
        ...item,
        key: `${item.status}-${item.id}-${index}`,
        action,
      }));
    },
    normalizeBulkActionIds(value) {
      if (!Array.isArray(value)) {
        return [];
      }

      return value
          .map((item) => {
            if (item && typeof item === "object") {
              return this.getModelKey(item);
            }

            return item;
          })
          .filter((item) => item !== null && item !== undefined && item !== "");
    },
    extractBulkActionCount(data, keys = []) {
      for (const key of keys) {
        const rawValue = this.lodash.get(data, key);
        const count = Number(rawValue);
        if (Number.isFinite(count)) {
          return count;
        }
      }

      return null;
    },
    extractBulkActionSummary(response, totalSelected) {
      const data = this.getBulkActionResponseData(response);
      const successItems = Array.isArray(data.success_items) ? data.success_items : [];
      const failedItems = Array.isArray(data.failed_items) ? data.failed_items : [];
      const successIds = this.normalizeBulkActionIds(successItems);
      const failedIds = this.normalizeBulkActionIds(failedItems);
      const successCount = this.extractBulkActionCount(data, [
        "success_count",
      ]) ?? (successIds.length > 0 ? successIds.length : null);
      const failedCount = this.extractBulkActionCount(data, [
        "failed_count",
      ]) ?? (failedIds.length > 0 ? failedIds.length : null);

      return {
        data,
        totalSelected,
        successItems,
        failedItems,
        successIds,
        failedIds,
        successCount: successCount ?? 0,
        failedCount: failedCount ?? 0,
        message: data.message || data?.data?.message || null,
      };
    },
    isBulkActionFullySuccessful(summary) {
      const total = this.getBulkActionTotalCount(summary);
      const successCount = Number(summary?.successCount || 0);
      const failedCount = Number(summary?.failedCount || 0);
      const successIdsCount = Array.isArray(summary?.successIds) ? summary.successIds.length : 0;

      if (total <= 0 || failedCount > 0) {
        return false;
      }

      if (successCount >= total || successIdsCount >= total) {
        return true;
      }

      return successCount === 0 && successIdsCount === 0;
    },
    showBulkActionSuccessModal(action, summary) {
      const total = this.getBulkActionTotalCount(summary) || this.selectedSalesPlanIds.length;
      const successCount = Number(summary?.successCount || 0);
      const resolvedCount = successCount > 0 ? successCount : total;
      const modalRef = this.$refs["sales-plan-bulk-action-success"];
      const title = this.getBulkActionSuccessTitle(action)
      const message = this.getBulkActionSuccessMessage(action, resolvedCount);

      if (!modalRef) {
        return Swal.fire({
          title: title + "!",
          text: message,
          type: "success",
          confirmButtonClass: "btn btn-success btn-fill",
          buttonsStyling: false
        });
      }

      return modalRef.show({
        title: title + "!",
        message,
      });
    },
    showBulkActionPartialSuccessModal(action, summary) {
      const total = this.getBulkActionTotalCount(summary) || this.selectedSalesPlanIds.length;
      const successCount = Number(summary?.successCount || 0);
      const modalRef = this.$refs["sales-plan-bulk-action-partial-success"];
      const title = this.getBulkActionPartialSuccessTitle(action, summary);
      const message = this.getBulkActionPartialSuccessMessage(action, successCount, total);
      const items = this.buildBulkActionResultItems(summary, action);

      if (!modalRef) {
        const safeHtmlItems = items.map((item) => {
          const safeLabel = this.lodash.escape(item.label || `ID ${item.id}`);
          const safeReason = item.reason ? this.lodash.escape(item.reason) : "";
          return `<div>[${this.lodash.escape(item.status)}] ${safeLabel}${safeReason ? ` - ${safeReason}` : ""}</div>`;
        }).join("");

        return Swal.fire({
          title: title + "!",
          html: `
            <div>${message}</div>
            ${items.length ? `<div class="mt-3">${safeHtmlItems}</div>` : ""}
          `,
          type: "warning",
          confirmButtonClass: "btn btn-warning btn-fill",
          buttonsStyling: false
        });
      }
      return modalRef.show({
        title: title + "!",
        message,
        items,
      });
    },
    handleBulkActionResultModalHidden() {
      this.$router.go(0);
    },
    async executeBulkSalesPlanAction(action, payload = {}) {
      const ids = [...this.selectedSalesPlanIds];
      const response = await this.$axios.post(
          this.getBulkActionEndpoint(action),
          this.buildActionPayload({
            ...payload,
            ids,
          })
      );
      const summary = this.extractBulkActionSummary(response, ids.length);

      await this.bulkActionSummaryAlert(action, summary);
    },
    bulkActionSummaryAlert(action, summary) {
      if (this.isBulkActionFullySuccessful(summary)) {
        return this.showBulkActionSuccessModal(action, summary);
      }

      return this.showBulkActionPartialSuccessModal(action, summary);
    },
    openBulkActionModal(action) {
      const total = this.selectedSalesPlanIds.length;
      if (action === "approve" && this.hasMixedSalesPlanApprovalLevels()) {
        return this.showMixedApprovalLevelWarning();
      }

      const modalRef = this.$refs[this.getBulkActionModalRef(action)];
      if (!modalRef) {
        return Promise.resolve(false);
      }

      return modalRef.show({
        title: this.getBulkActionConfirmTitle(action, total),
        message: this.getBulkActionConfirmMessage(action, total),
        approvalLevel: action === "approve" ? this.getSelectedSalesPlanApprovalLevels()[0] : null,
      }).then((result) => {
        if (!result) {
          return null;
        }

        const loadingKey = action === "approve" ? "bulkApproveLoading" : "bulkRejectLoading";
        this.$set(this, loadingKey, true);
        return this.executeBulkSalesPlanAction(action, result)
            .catch((error) => {
              this.failedAlert(error);
            })
            .finally(() => {
              this.$set(this, loadingKey, false);
            });
      });
    },
    handleBulkApprove() {
      if (!this.hasSelectedSalesPlans) return;
      return this.openBulkActionModal("approve");
    },
    handleBulkReject() {
      if (!this.hasSelectedSalesPlans) return;
      return this.openBulkActionModal("reject");
    },
    delete(value) {
      return this.$axios.delete(`/v1/sales-plans/${this.getModelKey(value)}`);
    },
    duplicateConfirmTitle() {
      return this.$t('sales_plans.modal_messages.confirm_duplicate_title');
    },
    duplicateConfirmDescription() {
      return this.$t('sales_plans.modal_messages.confirm_duplicate_description');
    },
    handleDelete(value) {
      this.deleteConfirmAlert().then(result => {
        if (result.value) {
          this.delete(value)
              .then(() => {
                this.selectedSalesPlanIds = this.selectedSalesPlanIds.filter(id => id !== this.getModelKey(value));
                return this.$refs.table.$refs.table.refresh();
              })
              .then(() => this.deleteSuccessAlert(this.getModelName(value)))
              .catch(error => {
                this.failedAlert(error);
                return null;
              });
        }
      });
    },
    handleCancel(value) {
      return this.$refs["sales-plan-cancel-confirmation"].show(value);
    },
    onCancelled() {
      return this.$refs.table?.$refs.table?.refresh();
    },
    buildSalesPlanFilterQueryParams() {
      return Object.entries(this.filterParams || {}).reduce((params, [key, value]) => {
        if (key === "status") {
          const statuses = expandSalesPlanStatusFilterValue(value);

          if (!statuses.length) {
            return params;
          }

          params[`filter[${key}]`] = statuses.join(',');
          return params;
        }

        params[`filter[${key}]`] = value;
        return params;
      }, {});
    },
    fetchModels(page = 1) {
      const filterQueryParams = this.buildSalesPlanFilterQueryParams();

      return this.$axios
          .get("/v1/sales-plans", {
            params: {
              page,
              per_page: this.perPage,
              search: this.search,
              sort: this.sortDesc ? `-${this.sortBy}`: this.sortBy,
              include: ["partner", "processing", "product", "farm"].join(","),
              ...this.$route.params,
              ...filterQueryParams,
            }
          })
          .then(response => {
            const {data: {data, meta}} = response;
            const currentPageIds = data.map(model => this.getModelKey(model));
            const currentPageSelectableIds = new Set(data.filter(model => this.canBulkSelect(model)).map(model => this.getModelKey(model)));

            this.selectedSalesPlanIds = this.selectedSalesPlanIds.filter(id => {
              return !currentPageIds.includes(id) || currentPageSelectableIds.has(id);
            });

            data.forEach(model => {
              const id = this.getModelKey(model);
              if (this.selectedSalesPlanIds.includes(id)) {
                this.$set(this.selectedSalesPlanApprovalLevels, id, this.getSalesPlanApprovalLevel(model));
              }
            });

            Object.keys(this.selectedSalesPlanApprovalLevels).forEach(id => {
              if (!this.selectedSalesPlanIds.includes(Number(id)) && !this.selectedSalesPlanIds.includes(id)) {
                this.$delete(this.selectedSalesPlanApprovalLevels, id);
              }
            });

            this.allowedActions = normalizeAllowedActions(meta?.allowed_actions);
            this.$set(this, "models", data.map(model => ({
              ...model,
              _selected: this.selectedSalesPlanIds.includes(this.getModelKey(model)),
              _canBulkSelect: this.canBulkSelect(model),
              _denyEdit: !this.hasAllowedAction(model, ["edit", "update"]),
              _denyDelete: !this.hasAllowedAction(model, ["delete", "destroy"]),
            })));
            this.page = Number(meta.current_page);
            this.perPage = Number(meta.per_page);
            this.from = Number(meta.from);
            this.total = Number(meta.total);
            this.stats = [
              {
                title: this.lodash.startCase(this.$t("sales_plans.widgets.estimated_sale_title")),
                value: amountScaleFormatter(meta?.total_estimated_sale ?? 0),
                valueTitle: amountScaleFormatter(meta?.total_estimated_sale ?? 0),
                wrapperClass: "col-12 col-md-6 col-xl-3",
                component: StatusWidget,
                icon: "bx bx-line-chart",
              },
              {
                title: this.lodash.startCase(this.$t("sales_plans.widgets.realized_amount_title")),
                value: amountScaleFormatter(meta?.total_realized_amount ?? 0),
                valueTitle: amountScaleFormatter(meta?.total_realized_amount ?? 0),
                wrapperClass: "col-12 col-md-6 col-xl-3",
                component: StatusWidget,
                icon: "bx bx-wallet",
              }
            ];

            return response;
          })
          .catch(error => {
            this.failedAlert(error);
          });
    },
    failedAlert(error) {
      return Swal.fire({
        title: this.$t("Failed") + "!",
        text: (error.response && (error.response.data || {}).message) || error.message,
        type: "danger",
        confirmButtonClass: "btn btn-danger btn-fill",
        buttonsStyling: false
      });
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  .page-content {
    background: #eef3f7 !important;
    font-family: Lato;
    color: #161b4d;
  }

  .text-right {
    text-align: right;
  }
}
</style>
<style>
body {
  max-width: 100vw !important;
  overflow-x: hidden !important;
}

.highlighted-row {
  background: #ddefff;
}

.highlighted-separator {
  border-bottom: 1px solid #1b77df;
  position: relative;

  .separator-badge {
    & ::before {
      content: "baru ditambahkan";
      position: absolute;
      bottom: -10px;
      border-radius: 4px;
      background: #1b77df;
      color: white;
      text-align: center;
      padding: 2px 5px;
      left: 0;
      font-size: 10px;
    }
  }
}

.highlighted-separator-badge {
  position: absolute;
  top: 100px;
  left: 100px;
  background: blue;
}
</style>
